import React, {
  MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import chartTheme from '@resources/js/components/Chart/ChartTheme';
import { type Options as HighchartsOptions } from 'highcharts';
import { merge, cloneDeep } from 'lodash';
import Highcharts from 'highcharts';
import LazyLoad from 'react-lazyload';
import useElementSize from '../../hooks/useElementSize';
import HighchartsXRange from 'highcharts/modules/xrange';
import HighchartsDrilldown from 'highcharts/modules/drilldown';
import HighchartsMore from 'highcharts/highcharts-more';
import useTimeLapse from '@resources/js/hooks/useTimeLapse';
import HighchartsReact from 'highcharts-react-official';

// required due to Highcharts not working during SSR
if (typeof window !== 'undefined') {
  HighchartsXRange(Highcharts);
  HighchartsDrilldown(Highcharts);
  HighchartsMore(Highcharts);
}

type OptionsWithTimeLapse = HighchartsOptions & {
  timeLapse: {
    dataPointKey: string;
    dataInterval: number;
    visualInterval: number;
    viewPortInterval: number;
    initialDelay: number;
    trailSeries: {
      dataInterval: number;
      color: string;
    };
    currentDataPointSeries: {
      type: 'scatter';
      marker: {
        lineColor: string;
        lineWidth: number;
        enabled: boolean;
        symbol: string;
        radius: number;
      };
    };
  };
};

export type ChartProps = {
  options: HighchartsOptions | OptionsWithTimeLapse;
  theme?: HighchartsOptions;
  lazyLoad?: boolean;
};

export default function Chart({
  options,
  lazyLoad = true,
  theme = chartTheme,
}: ChartProps) {
  const children =
    typeof window === 'undefined' ? null : (
      <ClientChart options={options} theme={theme} />
    );

  if (!lazyLoad) {
    return <>{children}</>;
  }

  return (
    <LazyLoad height={200} offset={50}>
      {children}
    </LazyLoad>
  );
}

/**
 * both highcharts-react and useElementSize use useLayoutEffect internally,
 * leading to React warnings during SSR
 */
function ClientChart({
  options: initialOptions,
  theme,
}: Pick<ChartProps, 'options' | 'theme'>) {
  const chartRef = useRef<HighchartsReact.RefObject | null>(null);
  const widthRef = useRef<HTMLDivElement | null>(null);
  const { width } = useElementSize(widthRef);
  const [lastChartWidth, setLastChartWidth] = useState(0);

  useEffect(() => {
    if (chartRef.current === null || width === 0 || width === lastChartWidth) {
      return;
    }
    setLastChartWidth(width);
    chartRef.current.chart.reflow();
  }, [lastChartWidth, width]);

  const options = useMemo<HighchartsOptions>(() => {
    // cloning required as merge mutates
    return merge(
      cloneDeep(theme),
      {
        title: {
          text: initialOptions?.title?.text ?? '',
        },
        credits: { enabled: false },
      },
      initialOptions
    );
  }, [initialOptions, theme]);

  return (
    <div ref={widthRef}>
      {hasTimeLapse(options) ? (
        <TimeLapseChart chartRef={chartRef} options={options} />
      ) : (
        <HighchartsReact
          options={options}
          highcharts={Highcharts}
          ref={chartRef}
        />
      )}
    </div>
  );
}

function hasTimeLapse(
  options: HighchartsOptions | OptionsWithTimeLapse
): options is OptionsWithTimeLapse {
  return Boolean(
    'timeLapse' in options &&
      options.timeLapse.dataPointKey &&
      options.timeLapse.dataInterval &&
      options.timeLapse.visualInterval
  );
}

type TimeLapseChartProps = {
  options: OptionsWithTimeLapse;
  chartRef: MutableRefObject<HighchartsReact.RefObject | null>;
};

function TimeLapseChart({
  options: initialOptions,
  chartRef,
}: TimeLapseChartProps) {
  const [options, setOptions] = useState(initialOptions);

  useTimeLapse(initialOptions, setOptions);

  return (
    <HighchartsReact options={options} highcharts={Highcharts} ref={chartRef} />
  );
}
